/**
 * Public CMS footer hook — fetches footer configuration from the CMS API.
 * Used by StorefrontLayout to render a dynamic footer.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FooterLink {
  label: string
  url: string
}

export interface FooterColumn {
  heading: string
  links: FooterLink[]
}

export interface SocialLink {
  platform: string
  url: string
}

export interface FooterConfig {
  columns: FooterColumn[]
  copyright_text: string
  social_links: SocialLink[]
  newsletter_enabled: boolean
}

export interface UseCmsFooterResult {
  footer: FooterConfig | null
  isLoading: boolean
  isError: boolean
}

// ─── Default config (when API returns empty or errors) ────────────────────────

const DEFAULT_FOOTER: FooterConfig = {
  columns: [],
  copyright_text: '',
  social_links: [],
  newsletter_enabled: false,
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCmsFooter(): UseCmsFooterResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cms-footer'],
    queryFn: async () => {
      const { data: response } = await api.get<{ data: FooterConfig }>('/cms/footer')
      return response.data ?? DEFAULT_FOOTER
    },
    staleTime: 30 * 1000, // 30 seconds — refetch frequently to pick up admin changes
    retry: 1,
  })

  return {
    footer: data ?? null,
    isLoading,
    isError,
  }
}
